import {
  Dispatch,
  MouseEvent,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { t } from 'i18next'
import {
  GridAlignment,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridPinnedColumnPosition,
  GridPinnedColumns,
  GridRenderCellParams,
  MuiEvent,
} from '@microservices/wiskey-react-table-component'
import { GridApiPro } from '@microservices/wiskey-react-table-component'
import { Interpolation, Theme } from '@mui/material'

import { EditCellRender } from '@pages/ConfiguredEntity/components/EditCellRender'
import { HeaderRender } from '@pages/ConfiguredEntity/components/HeaderRender'
import {
  SelectionCheckboxCell,
  SelectionCheckboxHeader,
} from '@pages/ConfiguredEntity/components/SelectionCheckbox/SelectionCheckbox'
import {
  applyAdminPinnedColumnVisibility,
  findActionByEventCode,
  getClassNameCell,
  getColumnFieldName,
  getColumnFieldNameForSort,
  getCssPropertiesFromColumnParameters,
  getHeaderClassName,
  getInitialColumnVisibilityModel,
  isColumnPinnedByAdmin,
  NAMES_OF_ENTITIES_FOR_STYLES,
  sortByOrder,
  transformFixRestrictions,
} from '@pages/ConfiguredEntity/helpers'
import getClassNameGlobalStyles from '@pages/ConfiguredEntity/helpers/getClassNameGlobalStyles'
import { OpenFormProps, unsavedChangesRefType } from '@pages/ConfiguredEntity/types'

import { useFetchObjectByCodeQuery } from '@redux/api'

import {
  cellRenderer,
  convertParameterPropertiesToCssProperties,
  generateDataGridActionsColumn,
  getActionBoolean,
  getStyleSettingsFromColumnDisplayRule,
} from '@helpers'
import {
  ACTION_CODE,
  ACTION_COLUMN_HEADER_CLASSNAME,
  BIND_TYPE,
  CLICK_EVENT_TYPE,
  DEFAULT_HEADER_STYLES_CLASSNAME,
  ENTITY,
  EVENT_CODE,
  MIN_VIEW_COLUMN_WIDTH,
  PINNED_COLUMN,
  STYLE_SETTING_CLASS_NAME_PREFIX,
  SYSTEM_SELECT_COLUMN,
} from '@constants'
import {
  ActionRow,
  ConfiguredColumn,
  EntityType,
  ObjectDataRecord,
  ParameterPropertyType,
  RestrictionData,
} from '@types'

type UseColumnsParams = {
  columns: GridColDef<ObjectDataRecord>[]
  onSetColumns: Dispatch<SetStateAction<GridColDef<ObjectDataRecord>[]>>
  type: ENTITY
  readonly?: boolean
  entity: EntityType
  actions?: ActionRow[]
  isViewDialogWindow?: boolean
  isListControlDialogWindow: boolean
  handleEdit: (
    params: GridCellParams<ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>
  ) => void
  handleRowButtonClick: (
    params: GridCellParams<ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>
  ) => void
  handleAdditionalButtonClick: (
    params: GridCellParams<ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>
  ) => void
  handleDownloadButtonClick: (
    params: GridCellParams<ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>,
    action?: ActionRow
  ) => void
  handlePreviewButtonClick: (
    params: GridCellParams<ObjectDataRecord>,
    event: MouseEvent<HTMLButtonElement>,
    action?: ActionRow
  ) => void
  handleClickDelete: (params?: GridCellParams<ObjectDataRecord>, action?: ActionRow) => void
  handleOpenForm?: (
    formProps: OpenFormProps,
    event: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>,
    type: CLICK_EVENT_TYPE
  ) => void
  setDropDownWindowOpen: (value: boolean) => void
  viewColumns: ConfiguredColumn[]
  hasQuickSearch: boolean
  setInitialColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>
  entityRestrictions?: RestrictionData
  handleSetPinnedColumns: (value: GridPinnedColumns) => void
  handleOpenTooltip: (event: MouseEvent<HTMLElement>, isHeader?: boolean) => void
  handleCloseTooltip: (event: MouseEvent<HTMLElement>) => void
  apiRef: MutableRefObject<GridApiPro>
  hasWrapHeaderText: boolean
  isEnabledLocalParameters: boolean
  isCtrlPressed: boolean
  isEditMode: boolean
  hasNewRows: boolean
  dialogId?: string
  hasRedifinitionActions?: boolean
  hasOnRowSelectionEvent?: boolean
  hasOuterOnRowSelectionEvent?: boolean
  hasExternalActionData?: boolean
  hasWrapRowText?: boolean
  unsavedChangesRef: unsavedChangesRefType
  defaultEntityRestrictions?: RestrictionData
  hasDragStartEvent?: boolean
  globalColumnTextStyles?: ParameterPropertyType[]
  globalColumnHeaderStyles?: ParameterPropertyType[]
  activeDownloadRows: Record<string, boolean>
  instanceId: string
  setSelectedRows: (selectedRows: ObjectDataRecord[]) => void
}

export const useColumns = ({
  columns,
  onSetColumns,
  type,
  readonly,
  entity,
  actions,
  isViewDialogWindow,
  isListControlDialogWindow,
  handleEdit,
  hasNewRows,
  handleAdditionalButtonClick,
  handleDownloadButtonClick,
  handlePreviewButtonClick,
  handleClickDelete,
  viewColumns,
  hasQuickSearch,
  setInitialColumnVisibilityModel,
  handleRowButtonClick,
  entityRestrictions,
  handleSetPinnedColumns,
  handleOpenTooltip,
  handleCloseTooltip,
  apiRef,
  hasWrapHeaderText,
  isEnabledLocalParameters,
  isCtrlPressed,
  isEditMode,
  setDropDownWindowOpen,
  dialogId,
  hasRedifinitionActions,
  hasExternalActionData,
  hasOnRowSelectionEvent,
  hasOuterOnRowSelectionEvent,
  unsavedChangesRef,
  hasWrapRowText,
  defaultEntityRestrictions,
  hasDragStartEvent,
  globalColumnHeaderStyles,
  globalColumnTextStyles,
  activeDownloadRows,
  instanceId,
  setSelectedRows,
}: UseColumnsParams) => {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({})
  const columnVisibilityEntityIdRef = useRef(entity?.id)
  const [globalStyles, setGlobalStyles] = useState<Interpolation<Theme>>({})

  // Колонка с чекбоксами у dropdown window
  const selectionColumn: GridColDef = useMemo(
    () => ({
      field: SYSTEM_SELECT_COLUMN,
      headerName: t('configuredView.select'),
      type: 'custom', // TODO ругается на select
      headerAlign: 'center',
      align: 'center',
      width: 75,
      resizable: false,
      hideable: false,
      pinnedColumn: PINNED_COLUMN.LEFT,
      disableColumnMenu: true,
      sortable: false,
      headerClassName: `${DEFAULT_HEADER_STYLES_CLASSNAME}`,
      renderCell: params => (
        <SelectionCheckboxCell isEditMode={isEditMode} readonly={readonly} row={params.row} />
      ),
      renderHeader: () => (
        <SelectionCheckboxHeader
          isEditMode={isEditMode}
          multiselection={entity.multiselection}
          readonly={readonly}
        />
      ),
    }),
    [readonly, isEditMode, entity.multiselection]
  )

  const { data: entityObjectModel, isLoading: isEntityObjectModelLoading } =
    useFetchObjectByCodeQuery(entity?.objectCode, {
      skip: !entity?.objectCode,
    })

  const areAllColumnsHidden = useMemo(
    () =>
      Object.keys(columnVisibilityModel).filter(modelKey => !columnVisibilityModel[modelKey])
        .length === viewColumns.length,
    [columnVisibilityModel, viewColumns]
  )

  const showColumnsButton = useMemo(
    () =>
      !viewColumns.some(
        column => column.bindType !== 'action' && (columnVisibilityModel[column.code] ?? true)
      ),
    [columnVisibilityModel, viewColumns]
  )

  const rowClickAction = findActionByEventCode(EVENT_CODE.ON_ROW_CLICK, actions)

  const rowCreateAction = useMemo(
    () =>
      actions?.find(
        action =>
          action.eventCode === EVENT_CODE.ON_ROW_CREATE &&
          (action.objectFormCode || action.params?.formCode)
      ),
    [actions]
  )

  const hasEntityRowAction = getActionBoolean(rowClickAction)

  const shouldDisplayCreateButton = getActionBoolean(rowCreateAction)

  const shouldDisplayNewLineButton = useMemo(
    () => actions?.some(action => action.actionCode === ACTION_CODE.OPEN_NEW_LINE),
    [actions]
  )

  // TODO: временное решение для экшенов получаемых из вне (конфигурация List Control в форме)
  // задача на реализацию конфигуратора - https://jiradc.utg.group/browse/WF-4982
  const columnsWithOuterActions = useMemo(() => {
    if (hasRedifinitionActions) {
      const rowDownloadButtonClickAction = findActionByEventCode(
        EVENT_CODE.ON_ROW_DOWNLOAD_BUTTON_CLICK,
        actions
      )

      const rowDeleteButtonClickAction = findActionByEventCode(
        EVENT_CODE.ON_ROW_DELETE_BUTTON_CLICK,
        actions
      )

      const isShowEdit = findActionByEventCode(EVENT_CODE.ON_ROW_EDIT, actions)

      const isShowAdditionalButton = findActionByEventCode(
        EVENT_CODE.ON_ROW_ADDITIONAL_BUTTON_CLICK,
        actions
      )

      const isShowRowDownloadButton = findActionByEventCode(
        EVENT_CODE.ON_ROW_DOWNLOAD_BUTTON_CLICK,
        actions
      )

      const isShowRowPreviewButton = findActionByEventCode(
        EVENT_CODE.ON_ROW_PREVIEW_BUTTON_CLICK,
        actions
      )

      const isShowSelectionColumn =
        hasExternalActionData ||
        hasOnRowSelectionEvent ||
        hasOuterOnRowSelectionEvent ||
        hasDragStartEvent

      const actionsColumn = generateDataGridActionsColumn({
        onEdit: isShowEdit ? handleEdit : undefined,
        onDelete: rowDeleteButtonClickAction
          ? params => handleClickDelete(params, rowDeleteButtonClickAction)
          : undefined,
        onAdditionalButtonClick: isShowAdditionalButton ? handleAdditionalButtonClick : undefined,
        onDowloadButtonClick: isShowRowDownloadButton
          ? (params, event) =>
              handleDownloadButtonClick(params, event, rowDownloadButtonClickAction)
          : undefined,
        onPreviewButtonClick: isShowRowPreviewButton
          ? (params, event) => handlePreviewButtonClick(params, event, rowDownloadButtonClickAction)
          : undefined,
        activeDownloadRows,
      })

      return [
        ...(isShowSelectionColumn ? [selectionColumn] : []),
        ...(actionsColumn ? [actionsColumn] : []),
        ...columns,
      ]
    }

    return [...columns]
  }, [columns, hasRedifinitionActions, selectionColumn, activeDownloadRows])

  const columnsForEntity = useMemo(() => {
    switch (type) {
      case ENTITY.DROP_DOWN_ENTITY:
        return [selectionColumn, ...columns]
      case ENTITY.LIST_CONTROL:
        return hasRedifinitionActions ? columnsWithOuterActions : columns
      case ENTITY.VIEW:
        return hasOnRowSelectionEvent || hasDragStartEvent ? [selectionColumn, ...columns] : columns
      default:
        return columns
    }
  }, [
    selectionColumn,
    type,
    areAllColumnsHidden,
    columns,
    hasOnRowSelectionEvent,
    hasRedifinitionActions,
  ])

  const columnsExist = !!columnsForEntity.length
  useEffect(() => {
    const displayRuleStyleSettingsCssPropertiesWithClassNames =
      getStyleSettingsFromColumnDisplayRule(
        viewColumns,
        `${STYLE_SETTING_CLASS_NAME_PREFIX}${instanceId}_`
      )

    const globalHeaderCssProperties =
      convertParameterPropertiesToCssProperties(globalColumnHeaderStyles)
    const globalTextCssProperties =
      convertParameterPropertiesToCssProperties(globalColumnTextStyles)

    const {
      headerCssProperties: viewHeaderCssProperties,
      textCssProperties: viewTextCssProperties,
    } = getCssPropertiesFromColumnParameters({
      parameters: entity.parameters,
      useParameters: entity.useLocalParameters,
    })

    const globalStylesNames = getClassNameGlobalStyles(
      NAMES_OF_ENTITIES_FOR_STYLES.GLOBAL,
      type,
      instanceId
    )
    const entityStylesNames = getClassNameGlobalStyles(
      NAMES_OF_ENTITIES_FOR_STYLES.ENTITY,
      type,
      instanceId
    )

    let styles = {
      '.cell--prefilled': {
        background: '#a7ffa370 !important',
      },
      '.cell--edited': {
        background: '#a7ffa3 !important',
        fontWeight: 'bold',
      },
      '.cell--error': {
        background: '#ffcfd6 !important',
      },

      [`.MuiDataGrid-root .MuiDataGrid-cell.${globalStylesNames.text}`]: globalTextCssProperties,
      [`.${globalStylesNames.header}`]: globalHeaderCssProperties,
      [`.MuiDataGrid-root.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-cell.${globalStylesNames.text}.MuiDataGrid-cell--pinnedLeft`]:
        globalTextCssProperties,
      [`.MuiDataGrid-root.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-cell.${globalStylesNames.text}.MuiDataGrid-cell--pinnedRight`]:
        globalTextCssProperties,

      [`.${entityStylesNames.header}`]: viewHeaderCssProperties,
      [`.MuiDataGrid-root .MuiDataGrid-cell.${entityStylesNames.text}`]: viewTextCssProperties,
      [`.MuiDataGrid-root.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-cell.${entityStylesNames.text}.MuiDataGrid-cell--pinnedLeft`]:
        viewTextCssProperties,
      [`.MuiDataGrid-root.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-cell.${entityStylesNames.text}.MuiDataGrid-cell--pinnedRight`]:
        viewTextCssProperties,

      ...displayRuleStyleSettingsCssPropertiesWithClassNames,
    }

    onSetColumns(
      sortByOrder(viewColumns).map((column, index) => {
        const currentColumn = columns?.at(index)

        const isColumnPinnedByAdminValue = isColumnPinnedByAdmin(
          column?.code,
          defaultEntityRestrictions
        )

        const {
          headerCssProperties: columnHeaderCssProperties,
          textCssProperties: columnTextCssProperties,
        } = getCssPropertiesFromColumnParameters({
          parameters: column.parameters,
          useParameters: column?.useLocalParameters,
        })

        const columnStylesNames = getClassNameGlobalStyles(
          NAMES_OF_ENTITIES_FOR_STYLES.COLUMN,
          type,
          instanceId,
          column.code
        )

        if (column?.useLocalParameters) {
          styles = {
            ...styles,
            [`.${columnStylesNames.header}`]: columnHeaderCssProperties,
            [`.MuiDataGrid-root .MuiDataGrid-cell.${columnStylesNames.text}`]:
              columnTextCssProperties,
            [`.MuiDataGrid-root.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-cell.${columnStylesNames.text}.MuiDataGrid-cell--pinnedLeft`]:
              columnTextCssProperties,
            [`.MuiDataGrid-root.MuiDataGrid-root.MuiDataGrid-root .MuiDataGrid-cell.${columnStylesNames.text}.MuiDataGrid-cell--pinnedRight`]:
              columnTextCssProperties,
          }
        }

        if (column.bindType === BIND_TYPE.ACTION) {
          const columnActions = actions?.filter(action =>
            column.actions?.some(colAction => colAction.id === action.id)
          )

          const rowClickColumnAction = findActionByEventCode(EVENT_CODE.ON_ROW_CLICK, columnActions)
          const hasRowAction = getActionBoolean(rowClickColumnAction)

          const rowDownloadButtonClickAction = findActionByEventCode(
            EVENT_CODE.ON_ROW_DOWNLOAD_BUTTON_CLICK,
            actions
          )

          const isShowRowDeleteButtonClick = findActionByEventCode(
            EVENT_CODE.ON_ROW_DELETE_BUTTON_CLICK,
            column.actions
          )

          const isShowEdit = findActionByEventCode(EVENT_CODE.ON_ROW_EDIT, column.actions)

          const isShowAdditionalButton = findActionByEventCode(
            EVENT_CODE.ON_ROW_ADDITIONAL_BUTTON_CLICK,
            column.actions
          )

          const isShowRowDownloadButton = findActionByEventCode(
            EVENT_CODE.ON_ROW_DOWNLOAD_BUTTON_CLICK,
            column.actions
          )

          const isShowRowPreviewButton = findActionByEventCode(
            EVENT_CODE.ON_ROW_PREVIEW_BUTTON_CLICK,
            column.actions
          )

          const actionColumn = {
            ...generateDataGridActionsColumn({
              onEdit: isShowEdit ? handleEdit : undefined,
              onDelete: isShowRowDeleteButtonClick ? handleClickDelete : undefined,
              onAdditionalButtonClick: isShowAdditionalButton
                ? handleAdditionalButtonClick
                : undefined,
              onDowloadButtonClick: isShowRowDownloadButton
                ? (params, event) =>
                    handleDownloadButtonClick(params, event, rowDownloadButtonClickAction)
                : undefined,
              onPreviewButtonClick: isShowRowPreviewButton ? handlePreviewButtonClick : undefined,
              onRowButtonClick: hasRowAction ? handleRowButtonClick : undefined,
              isDialogWindow: isViewDialogWindow || isListControlDialogWindow,
              activeDownloadRows,
            }),
          }

          return {
            ...actionColumn,
            field: column.code,
            id: column.id,
            internalId: column.internalId,
            headerName: column.title,
            width: column?.restrictions?.width ?? MIN_VIEW_COLUMN_WIDTH,
            order: column?.restrictions?.order ?? column.order,
            pinnedColumn: column.pinnedColumn,
            headerAlign: (columnHeaderCssProperties.textAlign ||
              viewHeaderCssProperties.textAlign ||
              globalHeaderCssProperties.textAlign) as GridAlignment,
            headerClassName: `${getHeaderClassName({
              entity,
              column,
              columnStylesNames,
              entityStylesNames,
              globalStylesNames,
            })} ${ACTION_COLUMN_HEADER_CLASSNAME}`,
            align: currentColumn?.align,
            cellClassName: params =>
              getClassNameCell({
                entity,
                column,
                params,
                columnStylesNames,
                entityStylesNames,
                globalStylesNames,
                instanceId,
              }),
          }
        }

        const shouldSort = column.bindType === BIND_TYPE.FIELD || !!column.columnToSort
        const cellObjectField = entityObjectModel?.fields?.find(
          field => field.name === column.value
        )

        const cellField = {
          bindType: column.bindType,
          code: column.code,
          objectCode: column.objectCode,
          objectValue: column.objectValue,
          type: column.fieldType,
          userRequired: cellObjectField?.required,
          value: column.value,
          valueFormat: column.valueFormat,
          valueType: column.valueType,
          enumDescription: column?.enumDescription,
        }

        return {
          id: column.id,
          internalId: column.internalId,
          field: getColumnFieldName(column),
          required: cellObjectField?.required,
          headerName: column.title,
          sortable: shouldSort,
          editable: entity?.editable && column.editable,
          hideable: !isColumnPinnedByAdminValue,
          objectCode: column.objectCode,
          objectValue: column.objectValue,
          preFill: column.preFill,
          // Если колонка закреплена администратором, то пользователь не может ее открепить/перезакрепить
          // pinnable: Boolean(column.pinnedColumn),
          sortKey: getColumnFieldNameForSort(column),
          columnValue: column.value,
          minWidth: MIN_VIEW_COLUMN_WIDTH,
          width: column?.restrictions?.width ?? MIN_VIEW_COLUMN_WIDTH,
          order: column?.restrictions?.order ?? column.order,
          value: column?.value,
          valueFormat: column?.valueFormat,
          valueType: column?.valueType,
          fieldType: column?.fieldType,
          renderCell: (params: GridRenderCellParams) => {
            return cellRenderer(column, params, dialogId, {
              shouldRenderObjectValueFromExpands: type === ENTITY.DROP_DOWN_ENTITY,
            })
          },
          renderEditCell: (params: GridRenderCellParams) => (
            <EditCellRender
              cellField={cellField}
              cellObjectFieldModel={cellObjectField?.model}
              hasNewRows={hasNewRows}
              hasWrapRowText={hasWrapRowText}
              isEditMode={isEditMode}
              isLoading={isEntityObjectModelLoading}
              params={params}
              setDropDownWindowOpen={setDropDownWindowOpen}
              unsavedChangesRef={unsavedChangesRef}
            />
          ),
          columnObjectValue: column.objectValue ?? '',
          renderHeader: params => (
            <HeaderRender
              apiRef={apiRef}
              column={column}
              handleCloseTooltip={handleCloseTooltip}
              handleOpenTooltip={handleOpenTooltip}
              hasQuickSearch={hasQuickSearch}
              hasWrapHeaderText={hasWrapHeaderText}
              isEnabledLocalParameters={isEnabledLocalParameters}
              params={params}
              showEditMarker={
                entity?.editable && column.editable && (isEditMode || hasNewRows || isCtrlPressed)
              }
            />
          ),
          // TODO Отключил flex, так-как он не работает с width, а свойство width необходимо для ресайза
          // flex: 1,
          // order: Math.round(Math.random() * 100),
          pinnedColumn: column.pinnedColumn,
          cellClassName: params =>
            getClassNameCell({
              entity,
              column,
              params,
              columnStylesNames,
              entityStylesNames,
              globalStylesNames,
              instanceId,
            }),
          headerClassName: getHeaderClassName({
            entity,
            column,
            columnStylesNames,
            entityStylesNames,
            globalStylesNames,
          }),
          headerAlign: (columnHeaderCssProperties.textAlign ||
            viewHeaderCssProperties.textAlign ||
            globalHeaderCssProperties.textAlign) as GridAlignment,
        }
      })
    )

    const initialColumnVisibilityModel = getInitialColumnVisibilityModel(
      viewColumns,
      undefined,
      undefined,
      defaultEntityRestrictions
    )

    setGlobalStyles(styles)

    setInitialColumnVisibilityModel(initialColumnVisibilityModel)
    setColumnVisibilityModel(prev => {
      if (entity?.id !== columnVisibilityEntityIdRef.current) {
        columnVisibilityEntityIdRef.current = entity?.id

        return applyAdminPinnedColumnVisibility(
          initialColumnVisibilityModel,
          viewColumns,
          defaultEntityRestrictions
        )
      }

      return applyAdminPinnedColumnVisibility(
        { ...initialColumnVisibilityModel, ...prev },
        viewColumns,
        defaultEntityRestrictions
      )
    })
  }, [
    entity,
    entityObjectModel,
    isEditMode,
    viewColumns,
    defaultEntityRestrictions,
    hasNewRows,
    isCtrlPressed,
    hasExternalActionData,
    hasOnRowSelectionEvent,
    activeDownloadRows,
  ])

  useEffect(() => {
    const isNeedPinSelectColumn = columnsForEntity.some(
      column => column.type === 'custom' && column.field === SYSTEM_SELECT_COLUMN
    )

    const pinnedColumns: GridPinnedColumns = {
      left: isNeedPinSelectColumn ? [SYSTEM_SELECT_COLUMN] : [],
      right: [],
    }

    entity?.columns?.forEach(column => {
      const pinnedColumn = column?.pinnedColumn

      if (pinnedColumn) {
        pinnedColumns[pinnedColumn]?.push(column.code)
      }
    })

    if (entityRestrictions && entityRestrictions.fix) {
      const transformedFixRestrictions = transformFixRestrictions(entityRestrictions.fix)

      Object.keys(pinnedColumns).forEach((leftRightKey: string) => {
        pinnedColumns[leftRightKey as GridPinnedColumnPosition]?.push(
          ...(transformedFixRestrictions[leftRightKey as GridPinnedColumnPosition] || [])
        )
      })
    }

    const sortPinnedColumns = (arr: string[]) => {
      if (!arr.length) {
        return arr
      }

      const visibleOrder =
        apiRef.current
          ?.getVisibleColumns?.()
          ?.map(col => col.field)
          .filter(Boolean) ?? []

      if (visibleOrder.length) {
        return [...arr].sort((a, b) => {
          const indexA = visibleOrder.indexOf(a)
          const indexB = visibleOrder.indexOf(b)

          if (indexA === -1 && indexB === -1) {
            return 0
          }
          if (indexA === -1) {
            return 1
          }
          if (indexB === -1) {
            return -1
          }

          return indexA - indexB
        })
      }

      const orderByField = new Map(
        viewColumns.map(col => [getColumnFieldName(col), Number(col.restrictions?.order ?? 0)])
      )

      return [...arr].sort(
        (a, b) => (orderByField.get(a) ?? Infinity) - (orderByField.get(b) ?? Infinity)
      )
    }

    // Во избежание дубля колонки при закреплении ее во вью и в конфигураторе
    const uniqPinnedColumns = {
      left: Array.from(new Set(sortPinnedColumns(pinnedColumns.left))),
      right: Array.from(new Set(sortPinnedColumns(pinnedColumns.right))),
    }

    handleSetPinnedColumns(uniqPinnedColumns)
  }, [entityRestrictions, entity, viewColumns])

  const isColumnReorderEnabled = type === ENTITY.VIEW || type === ENTITY.LIST_CONTROL

  return {
    columnsForEntity,
    areAllColumnsHidden,
    setColumnVisibilityModel,
    columnVisibilityModel,
    shouldDisplayCreateButton,
    rowCreateAction,
    hasRowAction: hasEntityRowAction,
    isColumnReorderEnabled,
    columnsExist,
    showColumnsButton,
    shouldDisplayNewLineButton,
    globalStyles,
  }
}
